
import { useState, useRef, useEffect, useCallback } from "react"
import { Button } from "antd"
import { CloseOutlined, MinusOutlined, BorderOutlined } from "@ant-design/icons"

const DraggableDialog = ({
  title,
  visible,
  onOk,
  onCancel,
  children,
  width = 520,
  height = 400,
  okText = "OK",
  cancelText = "Cancel",
  destroyOnClose = false,
}) => {
  const [position, setPosition] = useState({
    x: window.innerWidth / 2 - width / 2,
    y: window.innerHeight / 2 - height / 2,
  })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [isMinimized, setIsMinimized] = useState(false)
  const dialogRef = useRef(null)

  useEffect(() => {
    if (visible) {
      setPosition({
        x: window.innerWidth / 2 - width / 2,
        y: window.innerHeight / 2 - height / 2,
      })
      setIsMinimized(false)
    }
  }, [visible, width, height])

  const handleMouseDown = (e) => {
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect()
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      })
      setIsDragging(true)
    }
  }

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        })
      }
    },
    [isDragging, dragOffset],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])


  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized)
  }

  if (!visible) return null

  return (
    <div
      ref={dialogRef}
      style={{
        position: "fixed",
        zIndex: 1000,
        top: position.y,
        left: position.x,
        width: width,
        height: isMinimized ? 40 : height,
        backgroundColor: "#fff",
        borderRadius: 4,
        boxShadow:
          "0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "height 0.2s ease",
      }}
    >
      <div
        style={{
          padding: "10px 16px",
          borderBottom: isMinimized ? "none" : "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "move",
          backgroundColor: "#f5f5f5",
        }}
        onMouseDown={handleMouseDown}
      >
        <div style={{ fontWeight: 500 }}>{title}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="text"
            size="small"
            icon={isMinimized ? <BorderOutlined /> : <MinusOutlined />}
            onClick={toggleMinimize}
          />
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={onCancel} />
        </div>
      </div>

      {!isMinimized && (
        <>
          <div
            style={{
              padding: 16,
              flex: 1,
              overflowY: "auto",
            }}
          >
            {children}
          </div>

          <div
            style={{
              padding: "10px 16px",
              borderTop: "1px solid #f0f0f0",
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <Button onClick={onCancel}>{cancelText}</Button>
            <Button type="primary" onClick={onOk}>
              {okText}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default DraggableDialog
